import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { FileCheck } from 'lucide-react';

const CGU = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-yellow-50">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 mt-20">
        <Card className="max-w-4xl mx-auto shadow-xl">
          <CardHeader className="border-b">
            <div className="flex items-center gap-3">
              <FileCheck className="w-8 h-8 text-purple-600" />
              <CardTitle className="text-3xl font-bold">Conditions Générales d'Utilisation</CardTitle>
            </div>
          </CardHeader>
          
          <CardContent className="p-8 space-y-8">
            {/* Introduction */}
            <section>
              <p className="text-gray-700">
                Les présentes Conditions Générales d'Utilisation (CGU) régissent l'accès et l'utilisation de la plateforme Alliance Solidarité, éditée par l'association L'Alliance Humaine 2020.
              </p>
              <p className="text-gray-700 mt-2">
                En utilisant ce site, vous acceptez sans réserve les présentes CGU.
              </p>
            </section>

            {/* Objet */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-purple-600">1. Objet de la plateforme</h2>
              <p className="text-gray-700 mb-3">
                Alliance Solidarité est une plateforme solidaire permettant à ses utilisateurs de :
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Publier des offres solidaires (dons, services, entraide)</li>
                <li>Consulter et rechercher des offres</li>
                <li>Échanger avec d'autres membres via la messagerie</li>
                <li>Géolocaliser les offres sur une carte interactive</li>
              </ul>
            </section>

            {/* Accès */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-purple-600">2. Accès à la plateforme</h2>
              
              <h3 className="text-xl font-semibold mb-3 text-gray-800">2.1 Inscription</h3>
              <p className="text-gray-700 mb-3">
                L'utilisation de certaines fonctionnalités nécessite la création d'un compte utilisateur. Lors de l'inscription, vous vous engagez à :
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 mb-4">
                <li>Fournir des informations exactes et à jour</li>
                <li>Conserver la confidentialité de votre mot de passe</li>
                <li>Ne pas créer plusieurs comptes</li>
                <li>Ne pas usurper l'identité d'autrui</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 text-gray-800">2.2 Conditions d'accès</h3>
              <p className="text-gray-700">
                La plateforme est accessible gratuitement à toute personne physique majeure ou mineure avec autorisation parentale. L'association se réserve le droit de suspendre ou fermer un compte en cas de non-respect des CGU.
              </p>
            </section>

            {/* Utilisation */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-purple-600">3. Utilisation de la plateforme</h2>
              
              <h3 className="text-xl font-semibold mb-3 text-gray-800">3.1 Comportement</h3>
              <p className="text-gray-700 mb-3">Vous vous engagez à :</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>Utiliser la plateforme de manière respectueuse et solidaire</li>
                <li>Ne pas publier de contenu illégal, offensant, discriminatoire ou diffamatoire</li>
                <li>Respecter les droits de propriété intellectuelle</li>
                <li>Ne pas utiliser la plateforme à des fins commerciales non autorisées</li>
                <li>Ne pas tenter de nuire au fonctionnement de la plateforme</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 text-gray-800">3.2 Publication d'offres</h3>
              <p className="text-gray-700 mb-3">Les offres publiées doivent :</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Être claires, honnêtes et complètes</li>
                <li>Respecter la législation en vigueur</li>
                <li>Ne pas contenir de contenu inapproprié ou trompeur</li>
                <li>Être en lien avec l'esprit solidaire de la plateforme</li>
              </ul>
            </section>

            {/* Modération */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-purple-600">4. Modération</h2>
              <p className="text-gray-700 mb-3">
                L'association se réserve le droit de modérer tous les contenus publiés sur la plateforme. Nous pouvons :
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Supprimer ou modifier tout contenu contraire aux CGU</li>
                <li>Suspendre temporairement un compte</li>
                <li>Bannir définitivement un utilisateur en cas de manquement grave</li>
                <li>Signaler aux autorités compétentes tout contenu illégal</li>
              </ul>
              <p className="text-gray-700 mt-3">
                Les utilisateurs peuvent signaler tout contenu inapproprié via le bouton de signalement.
              </p>
            </section>

            {/* Propriété intellectuelle */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-purple-600">5. Propriété intellectuelle</h2>
              
              <h3 className="text-xl font-semibold mb-3 text-gray-800">5.1 Contenu de la plateforme</h3>
              <p className="text-gray-700 mb-4">
                L'ensemble du contenu de la plateforme (design, code, logos, marques) est la propriété exclusive de l'association L'Alliance Humaine 2020, sauf mention contraire.
              </p>

              <h3 className="text-xl font-semibold mb-3 text-gray-800">5.2 Contenu utilisateur</h3>
              <p className="text-gray-700">
                En publiant du contenu (offres, commentaires, messages), vous accordez à l'association une licence gratuite, non exclusive et mondiale pour utiliser, reproduire et diffuser ce contenu dans le cadre du fonctionnement de la plateforme. Vous conservez tous vos droits de propriété intellectuelle.
              </p>
            </section>

            {/* Responsabilité */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-purple-600">6. Responsabilité</h2>
              
              <h3 className="text-xl font-semibold mb-3 text-gray-800">6.1 Responsabilité de l'association</h3>
              <p className="text-gray-700 mb-3">
                L'association s'efforce d'assurer le bon fonctionnement de la plateforme. Toutefois :
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>La plateforme est fournie "en l'état", sans garantie de disponibilité permanente</li>
                <li>Nous ne garantissons pas l'exactitude des informations publiées par les utilisateurs</li>
                <li>Nous ne sommes pas responsables des transactions entre utilisateurs</li>
                <li>Nous ne sommes pas responsables des dommages résultant de l'utilisation de la plateforme</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 text-gray-800">6.2 Responsabilité de l'utilisateur</h3>
              <p className="text-gray-700">
                Vous êtes seul responsable de votre utilisation de la plateforme et des contenus que vous publiez. Vous vous engagez à indemniser l'association en cas de réclamation liée à votre utilisation de la plateforme.
              </p>
            </section>

            {/* Données personnelles */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-purple-600">7. Données personnelles</h2>
              <p className="text-gray-700">
                Le traitement de vos données personnelles est décrit dans notre <a href="/politique-confidentialite" className="text-purple-600 hover:underline">Politique de Confidentialité</a>, que vous acceptez en utilisant la plateforme.
              </p>
            </section>

            {/* Suspension de compte */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-purple-600">8. Suspension et résiliation</h2>
              
              <h3 className="text-xl font-semibold mb-3 text-gray-800">8.1 Par l'utilisateur</h3>
              <p className="text-gray-700 mb-4">
                Vous pouvez supprimer votre compte à tout moment depuis votre profil. Vos données seront supprimées conformément à notre politique de conservation.
              </p>

              <h3 className="text-xl font-semibold mb-3 text-gray-800">8.2 Par l'association</h3>
              <p className="text-gray-700 mb-3">
                Nous pouvons suspendre ou supprimer votre compte en cas de :
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Non-respect des CGU</li>
                <li>Comportement abusif ou frauduleux</li>
                <li>Inactivité prolongée (plus de 3 ans)</li>
                <li>Demande des autorités compétentes</li>
              </ul>
            </section>

            {/* Modifications */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-purple-600">9. Modifications des CGU</h2>
              <p className="text-gray-700">
                L'association se réserve le droit de modifier les présentes CGU à tout moment. Les utilisateurs seront informés des modifications par email et/ou notification sur le site. La poursuite de l'utilisation du site après modification vaut acceptation des nouvelles CGU.
              </p>
            </section>

            {/* Droit applicable */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-purple-600">10. Droit applicable et juridiction</h2>
              <p className="text-gray-700 mb-3">
                Les présentes CGU sont régies par le droit français. En cas de litige :
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Une solution amiable sera recherchée en priorité</li>
                <li>À défaut, les tribunaux français seront seuls compétents</li>
              </ul>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-purple-600">11. Contact</h2>
              <p className="text-gray-700">
                Pour toute question concernant ces CGU, vous pouvez nous contacter :
              </p>
              <div className="mt-3 p-4 bg-purple-50 rounded-lg text-gray-700">
                <p><strong>L'Alliance Humaine 2020</strong></p>
                <p>32 Allée Bajou, 94230 Cachan</p>
                <p>Email : <a href="mailto:contact@alliance-solidarite.org" className="text-purple-600 hover:underline">contact@alliance-solidarite.org</a></p>
                <p>Téléphone : 06 41 95 65 75</p>
              </div>
            </section>

            {/* Date */}
            <section className="pt-4 border-t">
              <p className="text-sm text-gray-500">
                Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            </section>
          </CardContent>
        </Card>
      </div>

      <Footer />
    </div>
  );
};

export default CGU;
